import cx from 'classnames';

import { Icon } from '@sb/design-system';

import type { ChatMessage } from './types';

interface UserMessageProps {
  message: ChatMessage;
  /**
   * Fires when the user taps the "Not sent. Tap to retry." affordance
   * under a failed bubble. Caller wires this to re-send the same
   * `message.text` through the chat transport. Absent for assistant
   * messages and for any message whose `sendStatus` is not 'failed'.
   */
  onRetry?: (messageID: string) => void;
}

/**
 * Right-aligned user bubble. Text is rendered as-is (no markdown);
 * explicit newlines round-trip via `whiteSpace: 'pre-wrap'`.
 *
 * When `message.sendStatus === 'failed'` the bubble renders with a
 * red exclamation icon to its left and a "Not sent. Tap to retry."
 * affordance directly below. Tapping the affordance (or the icon)
 * fires `onRetry(message.id)`. Used by UI-2.6's optimistic-send
 * failure UX.
 */
export function UserMessage({ message, onRetry }: UserMessageProps) {
  const isFailed = message.sendStatus === 'failed';

  return (
    <div className="tw-flex tw-flex-col tw-items-end">
      <div className="tw-flex tw-w-full tw-justify-end tw-items-center tw-gap-6">
        {isFailed && (
          <button
            type="button"
            onClick={() => onRetry?.(message.id)}
            aria-label="Retry sending"
            className={cx(
              'tw-flex',
              'tw-items-center',
              'tw-justify-center',
              'tw-shrink-0',
              'tw-bg-transparent',
              'tw-border-none',
              'tw-p-0',
              'tw-cursor-pointer',
            )}
          >
            <Icon
              kind="exclamationCircleFill"
              // 14×14 keeps the icon visually balanced with the 11px
              // retry label below; neither size is in the icon scale.
              style={{ width: 14, height: 14, color: 'var(--orange)' }}
            />
          </button>
        )}
        <div
          className={cx(
            'tw-rounded-18',
            'tw-py-10',
            'tw-whitespace-pre-wrap',
            'tw-break-words',
          )}
          style={{
            // Horizontal padding 14px, 85% max-width, and 14px font
            // don't map to design-system tokens. The bubble uses the
            // `--fill-primary` token (same as the composer pill) — a
            // translucent overlay that reads as a slightly-lighter
            // surface in both themes. `--surface-secondary` can't be used
            // here: in dark mode it resolves to the same gray as the
            // panel (`--surface-primary-elevated`), so the bubble would
            // vanish. Text uses `--label-primary`.
            paddingLeft: 14,
            paddingRight: 14,
            maxWidth: '85%',
            background: 'var(--fill-primary)',
            fontSize: 14,
            color: 'var(--label-primary)',
          }}
        >
          {message.text}
        </div>
      </div>
      {isFailed && (
        <button
          type="button"
          onClick={() => onRetry?.(message.id)}
          className={cx(
            'tw-bg-transparent',
            'tw-border-none',
            'tw-p-0',
            'tw-mt-2',
            'tw-cursor-pointer',
          )}
          style={{
            // 11px / inherit font / brand-red color aren't in the
            // typography scale; the affordance is intentionally smaller
            // than the bubble text so it reads as a hint, not content.
            fontSize: 11,
            fontFamily: 'inherit',
            color: 'var(--orange)',
          }}
        >
          Not sent. Tap to retry.
        </button>
      )}
    </div>
  );
}
